import { ReactElement } from 'react';

import { ActionText } from '@components/action-text';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconBrandMicrosoft } from '@components/icons/brand-microsoft';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconRemove } from '@components/icons/remove';
import { InfoBox } from '@components/info-box';
import { ListCard } from '@components/list-card';
import { RelativeSpinner } from '@components/loading-spinner';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { EnterpriseTag } from '@components/premium-tag/premium-tag';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useAzureAdSettings, useRemoveAzureAdSettings } from 'api/company/hooks';
import { useFeature } from 'ui/hooks';

import { useAzureADSettingsModal } from './modals/azure-sso-form';

export const Security = () => {
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasFeature } = useFeature('FEATURE_AZURE_SSO');

  return (
    <>
      <Header breadcrumbs={<Breadcrumb items={[{ label: t('security') }]} />} />
      <PageContent>
        <PageContainers.MiddleColumnPage>
          <SettingsSection
            title={t('security')}
            description={t('securityDescription')}
            hasSeparator
          >
            <AboutSection />
          </SettingsSection>
          <SettingsSection
            title={t('microsoftAzure')}
            titleTag={hasFeature ? undefined : <EnterpriseTag />}
            hasSeparator
          >
            <MicrosoftAzureSSO />
          </SettingsSection>
        </PageContainers.MiddleColumnPage>
      </PageContent>
    </>
  );
};

const MicrosoftAzureSSO = () => {
  const { t, styledTranslate } = useScopedTranslate(locale);
  const showSettingsModal = useAzureADSettingsModal();
  const showDeleteModal = useDeleteModal();
  const { data, isLoading } = useAzureAdSettings();
  const deleteMutation = useRemoveAzureAdSettings();
  const { isEnabled: hasFeature } = useFeature('FEATURE_AZURE_SSO');

  const isEnabled = Boolean(data?.clientId);

  return (
    <Stack space={4}>
      <Text
        font="regular14"
        color="textSecondary"
        text={styledTranslate('microsoftAzureDescription', {
          action: (
            <ActionText.ExternalLink
              href="https://help.journeys.50skills.app/en/articles/166916-setting-up-sso-with-azure-and-journeys"
              color="textSecondary"
              underline
              font="regular14"
            >
              {t('here')}
            </ActionText.ExternalLink>
          ),
        })}
      />
      {isLoading ? (
        <RelativeSpinner />
      ) : (
        <ListCard
          title={t('singleSignOn')}
          description={isEnabled ? t('enabled') : t('disabled')}
          icon={<IconBrandMicrosoft />}
          actionItems={
            isEnabled
              ? [
                  {
                    icon: <IconRemove />,
                    label: t('disableSSO'),
                    variant: 'danger',
                    onSelect: () =>
                      showDeleteModal({
                        onConfirm: () => deleteMutation.mutateAsync(),
                        description: t('disableSSODescription'),
                        title: t('disableSSO'),
                        buttonIcon: <IconRemove />,
                        buttonText: t('disable'),
                        hideCannotBeUndone: true,
                      }),
                  },
                ]
              : undefined
          }
          actions={
            isEnabled ? undefined : (
              <Button
                isDisabled={!hasFeature}
                variant="ghost"
                text={t('enable')}
                onClick={showSettingsModal}
              />
            )
          }
        />
      )}
    </Stack>
  );
};

const AboutSection = () => {
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasFeature } = useFeature('FEATURE_AZURE_SSO');
  if (hasFeature) {
    return null;
  }
  return (
    <InfoBox
      variant="indigo"
      icon={<IconFormIndicatorInfo color="indigo100" />}
      text={t('featureNotAvailable')}
    />
  );
};

type SectionProps = {
  description?: string;
  hasSeparator?: boolean;
  isBordered?: boolean;
  title?: string;
  titleTag?: ReactElement;
};

const SettingsSection: React.FC<SectionProps> = ({
  children,
  description,
  hasSeparator,
  isBordered,
  title,
  titleTag,
}) => {
  const Wrapper = isBordered ? Card : Stack;

  return (
    <>
      <Stack space={4}>
        <Stack space={2}>
          <Stack orientation="horizontal" space={2}>
            {title && <Text font="heading2" color="textPrimary" text={title} />}
            {titleTag}
          </Stack>
          {description && <Text font="regular14" color="textSecondary" text={description} />}
        </Stack>
        <Wrapper space={6} variant="transparent">
          <Stack space={4}>{children}</Stack>
        </Wrapper>
      </Stack>
      {hasSeparator && <Separator margin="1rem 0" />}
    </>
  );
};

export const locale = {
  en: {
    security: 'Security',
    securityDescription: "Manage your company's security settings and how users authenticate",
    featureNotAvailable: 'Single sign on is available on the enterprise plan',
    microsoftAzure: 'Microsoft Azure (SSO)',
    microsoftAzureDescription:
      'Enables users to log in using their Azure SSO credentials. Instructions on how to enable SSO can be found {action}.',
    here: 'here',
    singleSignOn: 'Single sign-on (SSO)',
    disableSSO: 'Disable SSO',
    disableSSODescription:
      'Are you sure you want to disable the Azure SSO sign-on option for your users? All the users that use SSO will be unable to sign in',
    enable: 'Enable',
    disable: 'Disable',
    enabled: 'Enabled',
    disabled: 'Disabled',
  },
};
